import { AssetType, ProductFamily } from '../../../../../types/api';
import {
  APPLICATION_BORROWER_BUSINESS_STRUCTURE,
  COMPANY_TYPES,
  FOR_PROFIT_COMPANY_TYPES,
  INDUSTRY_BORROWER_REPORTED_TYPES,
  LIMITS,
  NON_PROFIT_ORGANIZATION_TYPES,
} from '../../constants';
import { LOAN } from './constants';
import { ProductFamilies } from './productFamilies';
import type { PROJECT_TYPE } from './types';

export type BusinessTypeData = {
  forProfitCompanyType?: FOR_PROFIT_COMPANY_TYPES | string | null;
  nonProfitOrganizationType?: NON_PROFIT_ORGANIZATION_TYPES | string | null;
  industryBorrowerReported?: INDUSTRY_BORROWER_REPORTED_TYPES | string | null;
};

/**
 * Infer legacy `COMPANY_TYPES` from NFW borrower-type fields (inverse of Main Flow
 * `setBusinessTypeForProfitOrNonProfit` mapping).
 */
const getBusinessType = (data: BusinessTypeData): COMPANY_TYPES | undefined => {
  const {
    forProfitCompanyType,
    nonProfitOrganizationType,
    industryBorrowerReported,
  } = data;

  if (nonProfitOrganizationType === NON_PROFIT_ORGANIZATION_TYPES.HOA) {
    return COMPANY_TYPES.HOA;
  }

  if (
    industryBorrowerReported === INDUSTRY_BORROWER_REPORTED_TYPES.AGRICULTURE
  ) {
    return COMPANY_TYPES.AGRICULTURE;
  }

  if (
    forProfitCompanyType === FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS
  ) {
    return COMPANY_TYPES.PROPERTY_COMPANY;
  }

  if (
    forProfitCompanyType === FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES ||
    forProfitCompanyType ===
      FOR_PROFIT_COMPANY_TYPES.SELLS_SOLAR_GENERATED_POWER_TO_THIRD_PARTIES
  ) {
    return COMPANY_TYPES.OPERATING_COMPANY;
  }

  if (
    nonProfitOrganizationType === NON_PROFIT_ORGANIZATION_TYPES.FBO ||
    nonProfitOrganizationType === NON_PROFIT_ORGANIZATION_TYPES.EDU ||
    nonProfitOrganizationType === NON_PROFIT_ORGANIZATION_TYPES.OTHER
  ) {
    return COMPANY_TYPES.NON_PROFIT;
  }

  if (
    industryBorrowerReported === INDUSTRY_BORROWER_REPORTED_TYPES.NON_PROFIT
  ) {
    return COMPANY_TYPES.NON_PROFIT;
  }

  return undefined;
};

const NON_PROFIT_OR_HOA_BUSINESS_STRUCTURES: readonly string[] = [
  APPLICATION_BORROWER_BUSINESS_STRUCTURE.NON_PROFIT,
  APPLICATION_BORROWER_BUSINESS_STRUCTURE.HOA,
];

const PROPCO_ONLY_BUSINESS_STRUCTURES: readonly string[] = [
  APPLICATION_BORROWER_BUSINESS_STRUCTURE.IRREVOCABLE_TRUST,
  APPLICATION_BORROWER_BUSINESS_STRUCTURE.REVOCABLE_TRUST,
];

/** Visible Business Structure options: trusts only for PropCo; Non-Profit/HOA only for those borrowers. */
const getVisibleBusinessStructureChoices = <T extends { value: string }>(
  allChoices: T[],
  {
    isNonProfitOrHoa,
    isPropertyCompany,
  }: { isNonProfitOrHoa: boolean; isPropertyCompany: boolean }
): T[] =>
  allChoices.filter((choice) => {
    const isNonProfitOrHoaChoice =
      NON_PROFIT_OR_HOA_BUSINESS_STRUCTURES.includes(choice.value);
    const isPropCoOnlyChoice = PROPCO_ONLY_BUSINESS_STRUCTURES.includes(
      choice.value
    );

    if (isNonProfitOrHoa) return isNonProfitOrHoaChoice;
    if (isPropCoOnlyChoice) return isPropertyCompany;
    return !isNonProfitOrHoaChoice;
  });

/**
 * Solar or Solar+. Missing project type is not solar.
 */
const isSolar = (projectType: PROJECT_TYPE | null | undefined): boolean => {
  if (projectType == null) return false;
  return (LOAN.PROJECT_SOLAR_TYPES as readonly string[]).includes(projectType);
};

/** True only when the saved project type is Flex. */
const isFlexProject = (projectType: string | null | undefined): boolean =>
  projectType === LOAN.PROJECT_TYPE.FLEX;

/** Flex-product assets (no solar, not storage-only) get a lower floor. */
const minLoanAmount = (selectedAssetTypes: ReadonlyArray<AssetType>): number =>
  ProductFamilies.forAssetTypes(selectedAssetTypes).includes(ProductFamily.FLEX)
    ? LIMITS.MIN_FLEX_LOAN_AMOUNT
    : LIMITS.MIN_LOAN_AMOUNT;

export const LoanApplication = {
  getBusinessType,
  getVisibleBusinessStructureChoices,
  isSolar,
  isFlexProject,
  minLoanAmount,
};
